/**
 * 需求-响应矩阵工具（变化一：从"拼模板"到"精准响应"）
 *
 * 使用方式：
 *  1. 选择「管理项目」（自动带出该项目关联的知识库 / 企业知识库）；
 *  2. 上传「招标文件」与「投标文件」（文本 + 内嵌图片 OCR 一并参与）；
 *  3. 勾选「知识库」与「企业知识库」作为证据来源；
 *  4. 点击「生成矩阵」：后端异步任务跑「解析+OCR+抽要求+汇总证据+构建矩阵」，
 *     通过 SSE 实时推送进度（对齐文档瑕疵自检体验），避免同步请求超时。
 *
 * 对应后端 POST /api/content/response-matrix-build（异步）+ /response-matrix-task/{id}/stream。
 */
import { useEffect, useRef, useState } from 'react';
import {
  getErrorMessage,
  submitResponseMatrixBuild,
  subscribeResponseMatrixProgress,
  pollResponseMatrixStatus,
  fetchResponseMatrixResult,
  projectApi,
  knowledgeApi,
  companyApi,
} from '../../services/api';
import KnowledgeBaseSelectTab from '../KnowledgeBaseSelectTab';
import type { ProjectInfo, KnowledgeBase, CompanyProfile } from '../../types';
import {
  TableCellsIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

interface MatrixRow {
  requirement_id: string;
  title: string;
  responded: boolean;
  response_summary: string;
  evidence_source: string;
  evidence_quote: string;
  traceable: boolean;
  note: string;
  risk_level: string;
}
interface MatrixResult {
  rows: MatrixRow[];
  unresponded: string[];
  untraceable: { requirement_id: string; title: string; evidence_source: string; note: string }[];
  summary: string;
  risk_level: string;
}

const RISK_STYLE: Record<string, { label: string; cls: string }> = {
  high: { label: '高风险', cls: 'bg-red-100 text-red-700' },
  medium: { label: '中风险', cls: 'bg-amber-100 text-amber-700' },
  low: { label: '低风险', cls: 'bg-green-100 text-green-700' },
};

function CompanySelectTab({
  companies,
  selected,
  setSelected,
}: {
  companies: CompanyProfile[];
  selected: number[];
  setSelected: React.Dispatch<React.SetStateAction<number[]>>;
}) {
  if (companies.length === 0) {
    return <p className="text-xs text-gray-400 py-2">暂无可用的企业知识库，请在「我的设置 → 企业信息库」中添加</p>;
  }
  return (
    <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-lg divide-y divide-gray-100">
      {companies.map((c) => {
        const checked = selected.includes(c.id);
        return (
          <label key={c.id} className="flex items-center px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => setSelected((prev) => (checked ? prev.filter((id) => id !== c.id) : [...prev, c.id]))}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700 flex-1">{c.name}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function ResponseMatrix() {
  const [projects, setProjects] = useState<ProjectInfo[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | ''>('');
  const [tenderFile, setTenderFile] = useState<File | null>(null);
  const [bidFile, setBidFile] = useState<File | null>(null);

  const [knowledgeBaseIds, setKnowledgeBaseIds] = useState<number[]>([]);
  const [ownedKBs, setOwnedKBs] = useState<KnowledgeBase[]>([]);
  const [sharedKBs, setSharedKBs] = useState<KnowledgeBase[]>([]);

  const [companyIds, setCompanyIds] = useState<number[]>([]);
  const [ownedCompanies, setOwnedCompanies] = useState<CompanyProfile[]>([]);
  const [sharedCompanies, setSharedCompanies] = useState<CompanyProfile[]>([]);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<MatrixResult | null>(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const idleTimer = useRef<number | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [pl, okb, skb, ocp, scp] = await Promise.all([
          projectApi.list('managed'),
          knowledgeApi.list('owned'),
          knowledgeApi.list('shared'),
          companyApi.list('owned'),
          companyApi.list('shared'),
        ]);
        setProjects(pl.data.projects || []);
        setOwnedKBs(okb.data.knowledge_bases || []);
        setSharedKBs(skb.data.knowledge_bases || []);
        setOwnedCompanies(ocp.data.companies || []);
        setSharedCompanies(scp.data.companies || []);
      } catch (e) {
        setError(getErrorMessage(e));
      }
    };
    load();
  }, []);

  const handleProjectChange = async (id: number | '') => {
    setSelectedProjectId(id);
    if (!id) return;
    try {
      const p = await projectApi.get(id);
      setKnowledgeBaseIds(p.data.knowledge_base_ids || []);
      setCompanyIds(p.data.company_profile_ids || []);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const handleBuild = async () => {
    if (!tenderFile || !bidFile) {
      setError('请上传招标文件与投标文件。');
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    setProgress('已提交，后台处理中…');

    const token = localStorage.getItem('auth_token') || '';
    const fd = new FormData();
    fd.append('tender_file', tenderFile);
    fd.append('bid_file', bidFile);
    fd.append('knowledge_base_ids', knowledgeBaseIds.join(','));
    fd.append('company_profile_ids', companyIds.join(','));

    let tid = '';
    try {
      const data = await submitResponseMatrixBuild(fd);
      tid = data.task_id;
    } catch (e) {
      setError(getErrorMessage(e));
      setLoading(false);
      return;
    }

    const onDone = (payload: any) => {
      if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; }
      let resultData = payload?.result;
      const finish = (res?: any) => {
        if (res?.result) resultData = res.result;
        if (resultData) setResult(resultData);
        setLoading(false);
        setProgress('矩阵生成完成');
      };
      if (resultData) finish();
      else if (payload?.task_id) {
        fetchResponseMatrixResult(payload.task_id, token)
          .then(finish)
          .catch((err) => { setError(getErrorMessage(err)); setLoading(false); });
      } else {
        setLoading(false);
      }
    };

    const resetIdle = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = window.setTimeout(async () => {
        try {
          const s = await pollResponseMatrixStatus(tid, token);
          if (s?.status === 'done') onDone({ task_id: tid });
          else if (s?.status === 'error') { setError(s?.error || '后台任务失败'); setLoading(false); }
          else resetIdle();
        } catch {
          resetIdle();
        }
      }, 120000);
    };
    resetIdle();

    try {
      await subscribeResponseMatrixProgress(tid, token, (event: any) => {
        resetIdle();
        if (event.error || event.type === 'error') {
          setError(event.message || '后台任务失败');
          setLoading(false);
          return;
        }
        if (event.type === 'heartbeat') return;
        if (event.type === 'status_snapshot') {
          if (event.progress?.message) setProgress(event.progress.message);
          return;
        }
        if (event.type === 'phase' || event.type === 'progress') {
          if (event.message) setProgress(event.message);
          return;
        }
        if (event.type === 'done') {
          onDone(event);
        }
      });
    } catch (e) {
      // SSE 中断时依赖看门狗兜底轮询
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-1">
        <TableCellsIcon className="w-5 h-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-800">需求-响应矩阵</h2>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        对应「变化一：从拼模板到精准响应」。上传招标文件与投标文件，并选择管理项目 / 知识库 / 企业知识库作为证据来源，
        系统将自动抽取招标要求、逐条核对响应，并校验引用的企业资料证据是否能在原文中溯源（防止 AI 编造证据）。
        重流程走异步任务 + 实时进度，不会超时。
      </p>

      <div className="space-y-5">
        {/* 管理项目 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">管理项目（可选，自动带出关联的知识库 / 企业知识库）</label>
          <select
            value={selectedProjectId}
            onChange={(e) => handleProjectChange(e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="">— 不选 / 手动勾选知识库 —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* 文件上传 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">招标文件（含图片 OCR）</label>
            <input
              type="file"
              accept=".pdf,.docx,.txt,.md"
              onChange={(e) => setTenderFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {tenderFile && <p className="text-xs text-gray-400 mt-1">已选：{tenderFile.name}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">投标文件（含图片 OCR）</label>
            <input
              type="file"
              accept=".pdf,.docx,.txt,.md"
              onChange={(e) => setBidFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {bidFile && <p className="text-xs text-gray-400 mt-1">已选：{bidFile.name}</p>}
          </div>
        </div>

        {/* 知识库 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">知识库（作为证据来源，可多选）</label>
          <KnowledgeBaseSelectTab
            ownedKnowledgeBases={ownedKBs}
            sharedKnowledgeBases={sharedKBs}
            knowledgeBaseIds={knowledgeBaseIds}
            setKnowledgeBaseIds={setKnowledgeBaseIds}
          />
        </div>

        {/* 企业知识库 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">企业知识库（作为证据来源，可多选）</label>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-gray-500 mb-1">我的企业知识库</div>
              <CompanySelectTab companies={ownedCompanies} selected={companyIds} setSelected={setCompanyIds} />
            </div>
            {sharedCompanies.length > 0 && (
              <div>
                <div className="text-xs text-gray-500 mb-1">共享企业知识库</div>
                <CompanySelectTab companies={sharedCompanies} selected={companyIds} setSelected={setCompanyIds} />
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleBuild}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {loading ? '生成中...' : '生成矩阵'}
          </button>
          {loading && (
            <span className="text-sm text-gray-400 flex items-center gap-2">
              <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              {progress || '后台处理中…'}
            </span>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">{error}</div>
        )}
      </div>

      {result && (
        <div className="mt-6 space-y-5">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${RISK_STYLE[result.risk_level]?.cls || 'bg-gray-100 text-gray-600'}`}>
              {RISK_STYLE[result.risk_level]?.label || result.risk_level}
            </span>
            <span className="text-sm text-gray-700">{result.summary}</span>
          </div>

          {result.unresponded.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="text-sm font-medium text-red-700 mb-1">未响应的要求（{result.unresponded.length}）</div>
              <ul className="list-disc list-inside text-sm text-red-600">
                {result.unresponded.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          )}

          {result.untraceable.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <div className="text-sm font-medium text-amber-700 mb-1">证据无法溯源（疑似编造/来源缺失，{result.untraceable.length}）</div>
              <ul className="list-disc list-inside text-sm text-amber-600">
                {result.untraceable.map((u, i) => (
                  <li key={i}>{u.title}（来源：{u.evidence_source}）— {u.note}</li>
                ))}
              </ul>
            </div>
          )}

          <section>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">响应矩阵（{result.rows.length}）</h3>
            <div className="overflow-x-auto border border-gray-200 rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">要求</th>
                    <th className="px-3 py-2 text-left w-16">响应</th>
                    <th className="px-3 py-2 text-left">响应摘要</th>
                    <th className="px-3 py-2 text-left">证据来源</th>
                    <th className="px-3 py-2 text-left w-24">溯源</th>
                    <th className="px-3 py-2 text-left">说明</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((r, i) => {
                    const noEv = !r.evidence_source || r.evidence_source === '无';
                    return (
                      <tr key={i} className="border-t border-gray-100 align-top">
                        <td className="px-3 py-2 text-gray-800">{r.title}</td>
                        <td className="px-3 py-2">
                          {r.responded ? (
                            <CheckCircleIcon className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircleIcon className="w-5 h-5 text-red-500" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{r.response_summary || '—'}</td>
                        <td className="px-3 py-2 text-gray-600">{r.evidence_source || '—'}</td>
                        <td className="px-3 py-2">
                          {noEv ? (
                            <span className="text-gray-400 text-xs">无证据</span>
                          ) : r.traceable ? (
                            <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">可溯源</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">不可溯源</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">{r.note || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
